import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../ui/Section';
import { Download, CheckCircle2 } from 'lucide-react';
import { trackProfileDownload } from '../../hooks/useAnalytics';
const markets = ['Nigeria', 'South Africa', 'Ghana', 'Rwanda', 'Uganda', 'Tanzania', 'Egypt', 'Ethiopia'];
const DOWNLOAD_URL = 'https://drive.google.com/uc?export=download&id=1Glqy1vXt3eWEnz-RaAHY1uf77vsGLX0g';
const FILE_NAME = 'Belo_Digital_Company_Profile.pdf';
export function AfricanPresenceSection() {
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'done'>('idle');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number | undefined>(undefined);
  // Lock button width on mount so it doesn't shift between states
  useEffect(() => {
    if (buttonRef.current && !buttonWidth) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, [buttonWidth]);
  const handleDownload = async () => {
    if (downloadState !== 'idle') return;
    setDownloadState('downloading');
    trackProfileDownload();
    try {
      const res = await fetch(DOWNLOAD_URL);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = FILE_NAME;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloadState('done');
    } catch {
      const a = document.createElement('a');
      a.href = DOWNLOAD_URL;
      a.download = FILE_NAME;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloadState('done');
    }
    setTimeout(() => setDownloadState('idle'), 2500);
  };
  return <Section id="african-presence" background="default" data-id="element-91">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-92">
        {/* Left: Text */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} data-id="element-93">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-8" data-id="element-94">
            Our Global Presence
          </h2>

          <div className="space-y-6 text-gray-600 leading-relaxed text-base" data-id="element-95">
            <p data-id="element-96">
              Belo Digital is an African digital solutions company - proudly
              Kenyan-owned and based in Kenya, but built for the world. We
              combine local insights with global standards to create modern
              digital solutions that help businesses grow, connect, and thrive
              in today's fast-changing world.
            </p>
            <p data-id="element-97">
              Rooted in Kenya, Belo Digital is proud to serve businesses across
              Africa's growing digital landscape. Our presence spans key markets
              including{' '}
              <span className="text-[#FF6600] font-semibold" data-id="element-98">
                {markets.join(', ')}
              </span>
              , enabling us to deliver solutions that resonate with diverse
              audiences while embracing Africa's unique opportunities.
            </p>
          </div>

          <motion.button ref={buttonRef} onClick={handleDownload} disabled={downloadState !== 'idle'} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} style={buttonWidth ? {
          width: buttonWidth
        } : undefined} className={`relative mt-8 px-6 py-3 border rounded-lg text-sm font-medium overflow-hidden transition-colors duration-300 whitespace-nowrap ${downloadState === 'done' ? 'border-green-500 text-white' : downloadState === 'downloading' ? 'border-[#00316B] text-white cursor-wait' : 'border-[#00316B] text-[#00316B] hover:bg-[#00316B] hover:text-white'}`} data-id="element-99">
            {/* Fill bar — sweeps left to right during download */}
            <motion.div className="absolute inset-0 origin-left" initial={false} animate={downloadState === 'downloading' ? {
            scaleX: 1,
            backgroundColor: '#00316B'
          } : downloadState === 'done' ? {
            scaleX: 1,
            backgroundColor: '#22c55e'
          } : {
            scaleX: 0,
            backgroundColor: '#00316B'
          }} transition={downloadState === 'downloading' ? {
            scaleX: {
              duration: 2.5,
              ease: [0.4, 0, 0.2, 1]
            },
            backgroundColor: {
              duration: 0
            }
          } : downloadState === 'done' ? {
            backgroundColor: {
              duration: 0.3
            },
            scaleX: {
              duration: 0
            }
          } : {
            scaleX: {
              duration: 0.3
            },
            backgroundColor: {
              duration: 0
            }
          }} data-id="element-100" />

            {/* Text content */}
            <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-101">
              <AnimatePresence mode="wait" data-id="element-102">
                {downloadState === 'idle' && <motion.span key="idle" initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.15
              }} className="flex items-center gap-2" data-id="element-103">
                    Download Company Profile
                    <Download className="w-4 h-4" data-id="element-104" />
                  </motion.span>}
                {downloadState === 'downloading' && <motion.span key="downloading" initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.15
              }} className="flex items-center gap-2" data-id="element-105">
                    Downloading…
                  </motion.span>}
                {downloadState === 'done' && <motion.span key="done" initial={{
                opacity: 0,
                scale: 0.85
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.2,
                type: 'spring',
                stiffness: 350,
                damping: 20
              }} className="flex items-center gap-2" data-id="element-106">
                    <CheckCircle2 className="w-4 h-4" data-id="element-107" />
                    Downloaded!
                  </motion.span>}
              </AnimatePresence>
            </span>
          </motion.button>
        </motion.div>

        {/* Right: Map */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15,
        duration: 0.6
      }} className="flex items-center justify-center" data-id="element-108">
          <img src="https://cdn.magicpatterns.com/uploads/coGg12PdwFLJCnHNVDSovD/Africa.svg" alt="Map of Africa showing Belo Digital's presence" className="w-full max-w-md object-contain" data-id="element-109" />
        </motion.div>
      </div>
    </Section>;
}